import type { CSSProperties } from 'react';
import { Box, Text, color, config, toRem } from 'folds';
import { useSetting } from '$state/hooks/settings';
import { settingsAtom } from '$state/settings';

const unreadBadgeSize = toRem(18);

const unreadCountBadgeStyles = (borderColor: string): CSSProperties => ({
  pointerEvents: 'none',
  boxSizing: 'border-box',
  flexShrink: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: unreadBadgeSize,
  width: 'max-content',
  height: unreadBadgeSize,
  padding: `0 ${toRem(3)}`,
  borderRadius: config.radii.Pill,
  backgroundColor: color.Critical.Main,
  color: color.Critical.OnMain,
  whiteSpace: 'nowrap',
  borderWidth: config.borderWidth.B500,
  borderStyle: 'solid',
  borderColor,
});

export type UnreadCounts = {
  total: number;
  highlight: number;
};

type UnreadBadgeProps = {
  unread?: UnreadCounts;
  /** Show this number even when count badges are off. Used for invites. */
  count?: number;
  /** Count total unread instead of mentions. Still requires count badges to be on. */
  dm?: boolean;
  /** Outline color. Defaults to the container color. */
  borderColor?: string;
  /** Position at the bottom-right of a relatively positioned parent. */
  overlay?: boolean;
};

/**
 * Number a count badge should show. Zero means render nothing; halfdots are drawn separately.
 *
 * Mentions show a number when count badges are on. Direct messages show total unread in that case.
 */
export function unreadBadgeCount(
  unread: UnreadCounts | undefined,
  showCountBadges: boolean,
  dm?: boolean
): number {
  if (!unread) return 0;
  const total = Math.max(unread.total, unread.highlight);
  if (!showCountBadges) return 0;
  if (dm) return total;
  return unread.highlight > 0 ? unread.highlight : 0;
}

export function formatUnreadBadgeCount(count: number): string {
  return count > 99 ? '99+' : count.toString();
}

export function UnreadBadge({
  unread,
  count,
  dm,
  borderColor = color.Background.Container,
  overlay = false,
}: UnreadBadgeProps) {
  const [showCountBadges] = useSetting(settingsAtom, 'showCountBadges');
  const displayCount = count ?? unreadBadgeCount(unread, showCountBadges, dm);

  if (displayCount <= 0) return null;

  return (
    <Box
      aria-hidden
      style={{
        ...unreadCountBadgeStyles(borderColor),
        position: overlay ? 'absolute' : undefined,
        bottom: overlay ? toRem(0) : undefined,
        right: overlay ? toRem(0) : undefined,
        zIndex: overlay ? 1 : undefined,
      }}
    >
      <Text
        as="span"
        style={{
          fontSize: toRem(10),
          lineHeight: 1,
          fontWeight: config.fontWeight.W900,
        }}
      >
        {formatUnreadBadgeCount(displayCount)}
      </Text>
    </Box>
  );
}
